import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { deleteTagById } from 'api/tags';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Tag } from 'types/domain';

interface DeleteTagModalProps extends AlertDialog.AlertDialogRootProps {
    tag: Tag;
    handleViewContacts: (tagName: string) => void;
}

const ContactDeleteTagModal = ({
    tag,
    children,
    handleViewContacts,
    ...rest
}: DeleteTagModalProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const { mutate: onDeleteTag } = useMutation({
        mutationFn: () => deleteTagById(accountId, tag.id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.tags(accountId),
                type: 'all',
            });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            toast.success('Tag deleted successfully');
        },
        onError: () => {
            toast.error('Could not delete tag. Please try again.');
        },
    });

    return (
        <AlertDialog.Root type={'error'} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete Tag</AlertDialog.Title>
                        <AlertDialog.Description>
                            <p>
                                This action will delete{' '}
                                <span className="font-medium">{tag.name}</span> from{' '}
                                <span className="font-medium">
                                    {tag.object_count} {pluralize(tag.object_count || 0, 'contact')}
                                    .
                                </span>
                            </p>
                            {tag.object_count ? (
                                <Button
                                    variant="none"
                                    color="primary"
                                    size="md"
                                    onClick={() => handleViewContacts(tag.name)}
                                >
                                    View Contacts
                                </Button>
                            ) : null}
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => onDeleteTag()}>
                            Delete Tag
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteTagModalTrigger = AlertDialog.Trigger;

const Root = ContactDeleteTagModal;
const Trigger = DeleteTagModalTrigger;

export {
    ContactDeleteTagModal,
    DeleteTagModalTrigger,
    //
    Root,
    Trigger,
};
